/**
 * Every client logo, defined once — name, its colour file (src/images/
 * clients/Colored/) and its grey file (src/images/clients/gray/), read by
 * all four places a logo appears: the homepage/About Us marquee
 * (src/components/Clients.js), every industry "Trusted by" section
 * (src/components/industries/sections/TrustedBy.js) and the /caseStudies
 * story cards (src/components/caseStudies/AllCaseStudiesPage.js). Replaces
 * the old single-`file`-per-client src/data/clients.js now that each
 * client has two files, not one.
 *
 * `colorFile`/`grayFile` are paths relative to src/images/clients/ (as
 * src/components/clients/useClientLogoImages.js resolves them), or null
 * when that client has no file in that set. gray/ is the primary folder
 * everywhere a logo from this list is used — useClientLogos.js picks the
 * grey file first and only falls back to the colour one (CSS-grayscale-
 * filtered, so it still reads as grey) when there's no grey file yet.
 * Colour is never shown from this list, anywhere.
 *
 * The one place that DOES show real colour is each individual case study's
 * own page (src/pages/caseStudies/projects/*.js and src/data/caseStudies/
 * *.js) — those import their client's Colored/ file directly as a static
 * asset and don't read this list at all, so they're unaffected by it.
 *
 * `size` is an optional aspect-ratio-band override, as before; unset for
 * every entry below, same as the file this replaces.
 *
 * Matched colour file to grey file BY CLIENT, not always by filename —
 * most pairs share a filename stem (just a different extension), but four
 * don't (Centric Events, Muscat Media Group, Orange Line, ORR Protection)
 * and were matched by name or by opening the image; United Al-Zahra's grey
 * file has an extra "-uz" suffix on the same stem. Two colour files
 * couldn't be matched to any client with confidence and are left out here,
 * reported separately rather than guessed at: Colored/mtech.png ("mTech
 * Consulting Services" — not a current client) and Colored/orr-
 * corporation.png (a different, unrelated "ORR Corporation" logo, distinct
 * from the "ORR Protection" one actually used below). Colored/
 * Times_of_Oman.png + gray/Times_of_Oman.jpg are a clean, matched pair too,
 * but for a client ("Times of Oman") not currently in this list or
 * referenced anywhere on the site — left out for the same reason, not
 * added speculatively.
 *
 * This folder pair was being actively reorganised (files renamed to share
 * a stem with their counterpart) while this file was being written — it
 * was re-verified against disk immediately before being finalised, but if
 * anything moved again afterwards, a path below may be stale. Re-check
 * against src/images/clients/Colored/ and .../gray/ if a logo stops
 * resolving.
 *
 * Bhai Bhai Oil's colour file is the one exception still outside both new
 * folders — "bhai-bhai-oil.png" sits directly in src/images/clients/, not
 * in Colored/, and there's no grey version anywhere yet.
 *
 * EME Aquatic Systems is not in the old clients.js (it was only ever
 * referenced directly from src/data/caseStudies.js's own logoFile) — added
 * here too, colour only, so /caseStudies's card can resolve it through this
 * one shared mapping like every other story instead of a one-off path.
 * `caseStudyOnly: true` keeps it out of the homepage/About Us marquee
 * (src/components/Clients.js), which otherwise renders every entry here
 * unconditionally — EME was never part of that marquee and adding it
 * wasn't asked for.
 */
const CLIENT_LOGOS = [
  { name: "Centric Events", colorFile: "Colored/4099929_md.jpg", grayFile: "gray/Centric_Events_Rentals.jpg" },
  { name: "MIA Group of Companies", colorFile: "Colored/MIA_group_of_compnies.jpg", grayFile: "gray/MIA_group_of_compnies.jpg" },
  { name: "Cakes & Bakes", colorFile: "Colored/Cakes_Bakes.png", grayFile: "gray/Cakes_Bakes.jpg" },
  { name: "Ali Zaid Brothers", colorFile: "Colored/Ali_zaid_Brothers.png", grayFile: "gray/Ali_zaid_Brothers.jpg" },
  { name: "Alhamd Corporation", colorFile: "Colored/Alhamd_corporation.jpg", grayFile: "gray/Alhamd_corporation.jpg" },
  { name: "Averti Infotech", colorFile: null, grayFile: "gray/averti_infotech.jpg" },
  { name: "AXR Solution", colorFile: "Colored/AXR_solution.png", grayFile: "gray/AXR_solution.jpg" },
  { name: "Borjan", colorFile: "Colored/Borjan.jpg", grayFile: "gray/Borjan.jpg" },
  { name: "EcoZohm", colorFile: "Colored/Ecozohm.jpg", grayFile: "gray/Ecozohm.jpg" },
  { name: "SOI Northern", colorFile: "Colored/OIL_Northern.png", grayFile: "gray/OIL_Northern.jpg" },
  { name: "Evolved Office", colorFile: "Colored/evolved_office.png", grayFile: "gray/evolved_office.jpg" },
  { name: "Big Jerry's Fencing", colorFile: "Colored/BIG_jerry_fencing.png", grayFile: "gray/BIG_jerry_fencing.jpg" },
  { name: "Muscat Media Group", colorFile: "Colored/mediamuscatgroup.jpg", grayFile: "gray/Muscat_Media_Group.jpg" },
  { name: "Hush Puppies", colorFile: "Colored/Hush_Puppies.png", grayFile: "gray/Hush_Puppies.jpg" },
  { name: "Zie Pie", colorFile: null, grayFile: "gray/Zie_Pie.jpg" },
  { name: "ORR Protection", colorFile: "Colored/orrprotection.png", grayFile: "gray/orr_protection.jpg" },
  { name: "Oman Data Park", colorFile: null, grayFile: "gray/Oman_data_park.jpg" },
  { name: "Net Atlantic", colorFile: "Colored/Net_Atlantic.png", grayFile: "gray/Net_Atlantic.jpg" },
  { name: "Orange Line", colorFile: "Colored/orangeline_logo.png", grayFile: "gray/Orange_Line.jpg" },
  { name: "Renewell Water", colorFile: "Colored/rene_well_water.png", grayFile: "gray/rene_well_water.jpg" },
  { name: "Nurtureful Psychology", colorFile: "Colored/Nutureful.png", grayFile: "gray/Nutureful.jpg" },

  // The 15 "new logos" (added in an earlier ticket) plus EME.
  { name: "Aamaal Group", colorFile: "Colored/aamaal-group.png", grayFile: "gray/aamaal-group.png" },
  { name: "Huge Sports", colorFile: "Colored/huge-sports.png", grayFile: "gray/huge-sports.png" },
  { name: "Sohar Port & Freezone", colorFile: "Colored/sohar-port-freezone.png", grayFile: "gray/sohar-port-freezone.png" },
  { name: "United Al-Zahra", colorFile: "Colored/united-al-zahra.png", grayFile: "gray/united-al-zahra-uz.png" },
  { name: "United Gulf Pipe Manufacturing", colorFile: "Colored/united-gulf-pipe-manufacturing.png", grayFile: "gray/united-gulf-pipe-manufacturing.png" },
  { name: "Banu Mukhtar", colorFile: "Colored/banu-mukhtar.png", grayFile: "gray/banu-mukhtar.png" },
  { name: "Bhai Bhai Oil", colorFile: "bhai-bhai-oil.png", grayFile: null },
  { name: "IMC", colorFile: "Colored/imc.png", grayFile: null },
  { name: "Izhar Steel", colorFile: "Colored/izhar-steel.png", grayFile: null },
  { name: "Nestlé Waters", colorFile: "Colored/nestle-waters.png", grayFile: null },
  { name: "Olympia", colorFile: "Colored/olympia.png", grayFile: "gray/olympia.png" },
  { name: "State Bank of Pakistan", colorFile: "Colored/state-bank-of-pakistan.png", grayFile: null },
  { name: "Techno Trends", colorFile: "Colored/techno-trends.png", grayFile: null },
  { name: "Unilever", colorFile: "Colored/unilever.png", grayFile: null },
  { name: "Wall's", colorFile: "Colored/walls.png", grayFile: null },
  { name: "EME Aquatic Systems", colorFile: "Colored/EME-logo.png", grayFile: "gray/EME-logo.png", caseStudyOnly: true },
]

export default CLIENT_LOGOS
